import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, X, Save, Phone, Signal, Download, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Telecommunications = () => {
  const [contracts, setContracts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    phone_number: '',
    provider: '',
    contract_date: '',
    contract_end_date: '',
    employee_id: '',
    user_info: '',
    data_volume_gb: 0,
    network_type: 'LTE',
    monthly_cost: 0,
    status: 'aktiv',
    notes: ''
  });

  useEffect(() => {
    loadContracts();
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.filter(emp => emp.status === 'aktiv'));
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  };

  const loadContracts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/mobile-contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Verträge:', error);
      alert('Fehler beim Laden der Mobilfunkverträge');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('authToken');
      
      if (editingId) {
        await axios.put(
          `${BACKEND_URL}/api/mobile-contracts/${editingId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Vertrag erfolgreich aktualisiert!');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/mobile-contracts`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Vertrag erfolgreich erstellt!');
      }
      
      resetForm();
      loadContracts();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern des Vertrags');
    }
  };

  const handleEdit = (contract) => {
    setFormData({
      phone_number: contract.phone_number || '',
      provider: contract.provider || '',
      contract_date: contract.contract_date || '',
      contract_end_date: contract.contract_end_date || '',
      employee_id: contract.employee_id || '',
      user_info: contract.user_info || '',
      data_volume_gb: contract.data_volume_gb || 0,
      network_type: contract.network_type || 'LTE',
      monthly_cost: contract.monthly_cost || 0,
      status: contract.status || 'aktiv',
      notes: contract.notes || ''
    });
    setEditingId(contract.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diesen Vertrag wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${BACKEND_URL}/api/mobile-contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Vertrag erfolgreich gelöscht!');
      loadContracts();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Vertrags');
    }
  };

  const resetForm = () => {
    setFormData({
      phone_number: '',
      provider: '',
      contract_date: '',
      contract_end_date: '',
      employee_id: '',
      user_info: '',
      data_volume_gb: 0,
      network_type: 'LTE',
      monthly_cost: 0,
      status: 'aktiv',
      notes: ''
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return 'Nicht zugeordnet';
    const employee = employees.find(emp => emp.id === employeeId);
    if (employee) {
      return `${employee.first_name} ${employee.last_name}`;
    }
    return 'Nicht zugeordnet';
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/mobile-contracts/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mobilfunkvertraege_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('Export erfolgreich!');
    } catch (error) {
      console.error('Fehler beim Export:', error);
      alert('Fehler beim Export');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Bitte wählen Sie eine Datei aus');
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/mobile-contracts/import/excel`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      alert(`Import erfolgreich!\nNeu importiert: ${response.data.imported}\nAktualisiert: ${response.data.updated}${response.data.errors.length > 0 ? '\n\nFehler:\n' + response.data.errors.join('\n') : ''}`);
      setShowImportDialog(false);
      setImportFile(null);
      loadContracts();
    } catch (error) {
      console.error('Fehler beim Import:', error);
      alert(error.response?.data?.detail || 'Fehler beim Import');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Phone className="w-8 h-8" />
          Telekommunikation
        </h1>
        <div className="flex gap-3">
          <button
            onClick={handleExport}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Excel exportieren
          </button>
          <button
            onClick={() => setShowImportDialog(true)}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Excel importieren
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Neuer Vertrag
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              {editingId ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}
            </h2>
            <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Telefonnummer *</label>
              <input
                type="text"
                required
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                className="w-full p-2 border rounded"
                placeholder="z.B. +49 123 456789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Anbieter *</label>
              <input
                type="text"
                required
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                className="w-full p-2 border rounded"
                placeholder="z.B. Telekom, Vodafone"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Vertragsdatum *</label>
              <input
                type="date"
                required
                value={formData.contract_date}
                onChange={(e) => setFormData({ ...formData, contract_date: e.target.value })}
                className="w-full p-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Vertragsende</label>
              <input
                type="date"
                value={formData.contract_end_date}
                onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                className="w-full p-2 border rounded"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Mitarbeiter *</label>
              <select
                required
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                className="w-full p-2 border rounded"
              >
                <option value="">-- Mitarbeiter auswählen --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} ({emp.employee_number})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Datenvolumen (GB) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.1"
                value={formData.data_volume_gb}
                onChange={(e) => setFormData({ ...formData, data_volume_gb: parseFloat(e.target.value) })}
                className="w-full p-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Netzwerktyp *</label>
              <select
                value={formData.network_type}
                onChange={(e) => setFormData({ ...formData, network_type: e.target.value })}
                className="w-full p-2 border rounded"
              >
                <option value="5G">5G</option>
                <option value="LTE">LTE</option>
                <option value="4G">4G</option>
                <option value="3G">3G</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Monatliche Kosten (€)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.monthly_cost}
                onChange={(e) => setFormData({ ...formData, monthly_cost: parseFloat(e.target.value) })}
                className="w-full p-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full p-2 border rounded"
              >
                <option value="aktiv">Aktiv</option>
                <option value="inaktiv">Inaktiv</option>
                <option value="gekündigt">Gekündigt</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Notizen</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full p-2 border rounded"
                rows="2"
              />
            </div>

            <div className="col-span-2 flex gap-3 justify-end">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editingId ? 'Aktualisieren' : 'Erstellen'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Contracts List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Telefonnummer</th>
              <th className="p-3 text-left">Anbieter</th>
              <th className="p-3 text-left">Benutzer</th>
              <th className="p-3 text-left">Datenvolumen</th>
              <th className="p-3 text-left">Netzwerk</th>
              <th className="p-3 text-left">Kosten/Monat</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {contracts.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-6 text-center text-gray-500">
                  Keine Verträge vorhanden
                </td>
              </tr>
            ) : (
              contracts.map((contract) => (
                <tr key={contract.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 font-medium">{contract.phone_number}</td>
                  <td className="p-3">{contract.provider}</td>
                  <td className="p-3">{getEmployeeName(contract.employee_id)}</td>
                  <td className="p-3">{contract.data_volume_gb} GB</td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1">
                      <Signal className="w-4 h-4" />
                      {contract.network_type}
                    </span>
                  </td>
                  <td className="p-3">€ {contract.monthly_cost?.toFixed(2) || '0.00'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      contract.status === 'aktiv' ? 'bg-green-100 text-green-800' :
                      contract.status === 'inaktiv' ? 'bg-gray-100 text-gray-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {contract.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(contract)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(contract.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Import Dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Excel importieren</h2>
              <button 
                onClick={() => {
                  setShowImportDialog(false);
                  setImportFile(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Laden Sie eine Excel-Datei mit Mobilfunkverträgen hoch. Die Datei sollte folgende Spalten enthalten:
                </p>
                <ul className="text-sm text-gray-600 list-disc list-inside mb-4">
                  <li>Telefonnummer</li>
                  <li>Anbieter</li>
                  <li>Vertragsdatum</li>
                  <li>Vertragsende</li>
                  <li>Mitarbeiter ID</li>
                  <li>Datenvolumen (GB)</li>
                  <li>Netzwerktyp</li>
                  <li>Monatliche Kosten (€)</li>
                  <li>Status</li>
                  <li>Notizen</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Excel-Datei auswählen
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="w-full p-2 border rounded"
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button 
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportFile(null);
                  }}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Abbrechen
                </button>
                <button 
                  onClick={handleImport}
                  disabled={!importFile || importing}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="w-4 h-4" />
                  {importing ? 'Importiere...' : 'Importieren'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Telecommunications;
